import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { dmYhm } from "@/lib/dateFmt";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import EmptyState from "@/components/EmptyState";
import { toast } from "sonner";
import { ArrowClockwise, MagnifyingGlass } from "@phosphor-icons/react";

export default function ActivityLog() {
    const [activity, setActivity] = useState([]);
    const [logins, setLogins] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [q, setQ] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;
            const [a, l] = await Promise.all([
                api.get("/activity-log", { params }),
                api.get("/login-log"),
            ]);
            setActivity(a.data);
            setLogins(l.data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const filtered = React.useMemo(() => {
        const t = q.trim().toLowerCase();
        if (!t) return activity;
        return activity.filter((a) =>
            (a.user_email_current || a.user_email || "").toLowerCase().includes(t) ||
            (a.user_name || "").toLowerCase().includes(t) ||
            (a.action || "").toLowerCase().includes(t) ||
            (a.details || "").toLowerCase().includes(t)
        );
    }, [activity, q]);

    const clearRange = () => { setDateFrom(""); setDateTo(""); setTimeout(load, 0); };

    return (
        <div className="space-y-6" data-testid="activity-page">
            <div>
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Audit</div>
                <h1 className="mt-1 font-display text-4xl font-bold tracking-tight text-slate-900">Activity Log</h1>
            </div>

            <Tabs defaultValue="activity">
                <TabsList>
                    <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
                    <TabsTrigger value="logins" data-testid="tab-logins">Login History</TabsTrigger>
                </TabsList>
                <TabsContent value="activity" className="mt-6">
                    <div className="mb-4 flex flex-wrap items-end gap-2 rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
                        <div>
                            <Label className="text-[10px] uppercase tracking-[0.15em] text-slate-500">From</Label>
                            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="activity-from" className="mt-1 w-40" />
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase tracking-[0.15em] text-slate-500">To</Label>
                            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="activity-to" className="mt-1 w-40" />
                        </div>
                        <Button onClick={load} disabled={loading} data-testid="activity-apply" className="bg-slate-900 text-white hover:bg-slate-800">
                            <ArrowClockwise size={13} className="mr-1.5" />{loading ? "Loading…" : "Apply"}
                        </Button>
                        {(dateFrom || dateTo) && (
                            <button type="button" onClick={clearRange} className="text-xs text-slate-500 underline underline-offset-2 hover:text-slate-800">Clear range</button>
                        )}
                        <div className="relative ml-auto flex-1 max-w-sm">
                            <MagnifyingGlass size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by user, action, details…" className="pl-9" data-testid="activity-search" />
                        </div>
                        <div className="text-[11px] text-slate-500">Showing {filtered.length} of {activity.length}</div>
                    </div>

                    {filtered.length === 0 ? <EmptyState title="No activity in this window" /> : (
                        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                                            <th className="px-5 py-3 font-medium">Time</th>
                                            <th className="px-5 py-3 font-medium">User</th>
                                            <th className="px-5 py-3 font-medium">Action</th>
                                            <th className="px-5 py-3 font-medium">Details</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filtered.map((a) => (
                                            <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`activity-row-${a.id}`}>
                                                <td className="px-5 py-3 text-slate-600 whitespace-nowrap">{dmYhm(a.timestamp)}</td>
                                                <td className="px-5 py-3">
                                                    <div className="text-slate-900 font-medium">{a.user_name || <span className="italic text-slate-400">— no name —</span>}</div>
                                                    <div className="text-[11px] text-slate-500 font-mono">{a.user_email_current || a.user_email || ""}</div>
                                                </td>
                                                <td className="px-5 py-3"><span className="rounded bg-slate-100 px-2 py-0.5 font-mono text-xs">{a.action}</span></td>
                                                <td className="px-5 py-3 text-slate-600 max-w-[36rem] whitespace-pre-line">{a.details}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </TabsContent>
                <TabsContent value="logins" className="mt-6">
                    {logins.length === 0 ? <EmptyState title="No login records" /> : (
                        <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                                        <th className="px-5 py-3 font-medium">Time</th>
                                        <th className="px-5 py-3 font-medium">Email</th>
                                        <th className="px-5 py-3 font-medium">IP</th>
                                        <th className="px-5 py-3 font-medium">Result</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logins.map((l) => (
                                        <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50">
                                            <td className="px-5 py-3 text-slate-600">{dmYhm(l.timestamp)}</td>
                                            <td className="px-5 py-3 text-slate-800">{l.email}</td>
                                            <td className="px-5 py-3 font-mono text-xs text-slate-500">{l.ip || "—"}</td>
                                            <td className="px-5 py-3">
                                                <span className={`rounded-full border px-2 py-0.5 text-xs ${l.success ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-rose-200 bg-rose-50 text-rose-700"}`}>
                                                    {l.success ? "Success" : "Failed"}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </TabsContent>
            </Tabs>
        </div>
    );
}
